/**
 * Permissions Hooks
 *
 * React hooks for permission management using TanStack Query
 * 
 * Reference API format:
 * GET /permissions?fields=*&filter[_and][0][policy]=uuid&page=0&limit=-1
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';
import {
    useAccessControlStore,
    type Permission,
} from '@/stores/access-control';
import { buildQueryString, buildFilterParams } from './query-utils';

// ============== Types ==============

export interface PermissionInput {
    policy: string;
    collection: string;
    action: 'create' | 'read' | 'update' | 'delete' | 'share';
    permissions?: Record<string, unknown> | null;
    validation?: Record<string, unknown> | null;
    presets?: Record<string, unknown> | null;
    fields?: string[] | null;
}

export interface PermissionFilter {
    policy?: string;
    collection?: string;
    action?: string;
}

// ============== Hooks ==============

/**
 * Fetch all permissions with optional filters
 * 
 * Uses the reference CMS query format:
 * /permissions?fields=*&filter[_and][0][policy]=uuid&page=0&limit=-1
 */
export function usePermissions(filter?: PermissionFilter) {
    const { setPermissions, setPermissionsLoading } = useAccessControlStore();

    return useQuery({
        queryKey: ['permissions', filter],
        queryFn: async () => {
            setPermissionsLoading(true);
            try {
                // Build query params in reference CMS format
                const queryParams: Record<string, unknown> = {
                    fields: '*',
                    page: 0,
                    limit: -1,
                };

                // Add filter if provided
                if (filter && Object.keys(filter).length > 0) {
                    const filterParams = buildFilterParams(filter as Record<string, unknown>);
                    Object.assign(queryParams, filterParams);
                }

                const queryString = buildQueryString(queryParams);
                const response = await api.get<ApiListResponse<Permission>>(`/permissions?${queryString}`);
                const permissionsPayload = response.data as unknown;
                const permissions = Array.isArray(permissionsPayload)
                    ? permissionsPayload
                    : (permissionsPayload as { data?: Permission[] | null })?.data ?? [];
                setPermissions(permissions);
                return permissions;
            } finally {
                setPermissionsLoading(false);
            }
        },
    });
}

/**
 * Fetch permissions for a specific policy
 * 
 * Shorthand for usePermissions({ policy: policyId })
 */
export function usePolicyPermissions(policyId: string | undefined) {
    return usePermissions(policyId ? { policy: policyId } : undefined);
}

/**
 * Get current user's permissions
 */
function normalizePermissions(payload: unknown): Permission[] {
    // If already array, ensure access defaults to 'full'
    if (Array.isArray(payload)) {
        return payload.map((p) => ({ access: 'full', ...(p as Permission) }));
    }

    // /permissions/me returns CollectionAccess map { [collection]: { create: { access, fields... }, ... } }
    if (payload && typeof payload === 'object') {
        const entries = Object.entries(payload as Record<string, unknown>);
        const mapped: Permission[] = [];

        for (const [collection, actions] of entries) {
            if (!actions || typeof actions !== 'object') continue;
            for (const [action, detail] of Object.entries(actions as Record<string, unknown>)) {
                if (!detail || typeof detail !== 'object') continue;
                mapped.push({
                    // @ts-expect-error action type narrowing
                    action,
                    collection,
                    access: (detail as { access?: string }).access ?? 'none',
                    policy: '',
                    permissions: (detail as { permissions?: Record<string, unknown> | null }).permissions ?? null,
                    validation: (detail as { validation?: Record<string, unknown> | null }).validation ?? null,
                    presets: (detail as { presets?: Record<string, unknown> | null }).presets ?? null,
                    fields: (detail as { fields?: string[] | null }).fields ?? null,
                    id: `${collection}-${action}`,
                });
            }
        }

        return mapped;
    }

    return [];
}

export function useMyPermissions() {
    return useQuery({
        queryKey: ['permissions', 'me'],
        queryFn: async () => {
            const response = await api.get<ApiListResponse<Permission>>('/permissions/me');
            const payload = response.data as unknown;
            const data = (payload as { data?: unknown })?.data ?? payload;
            return normalizePermissions(data);
        },
    });
}

/**
 * Fetch single permission by ID
 */
export function usePermission(id: string | undefined) {
    return useQuery({
        queryKey: ['permissions', 'single', id],
        queryFn: async () => {
            if (!id) return null;
            const response = await api.get<ApiResponse<Permission>>(`/permissions/${id}`);
            return response.data || null;
        },
        enabled: !!id,
    });
}

/**
 * Create a permission
 */
export function useCreatePermission() {
    const queryClient = useQueryClient();
    const { addPermission } = useAccessControlStore();

    return useMutation({
        mutationFn: async (data: PermissionInput) => {
            const response = await api.post<ApiResponse<Permission>>('/permissions', data);
            return response.data;
        },
        onSuccess: (permission) => {
            if (permission) addPermission(permission);
            queryClient.invalidateQueries({ queryKey: ['permissions'] });
        },
    });
}

/**
 * Update a permission
 */
export function useUpdatePermission() {
    const queryClient = useQueryClient();
    const { updatePermission } = useAccessControlStore();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<PermissionInput> }) => {
            const response = await api.patch<ApiResponse<Permission>>(`/permissions/${id}`, data);
            return response.data;
        },
        onSuccess: (permission) => {
            if (permission) updatePermission(permission.id, permission);
            queryClient.invalidateQueries({ queryKey: ['permissions'] });
        },
    });
}

/**
 * Delete a permission
 */
export function useDeletePermission() {
    const queryClient = useQueryClient();
    const { removePermission } = useAccessControlStore();

    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/permissions/${id}`);
            return id;
        },
        onSuccess: (id) => {
            removePermission(id);
            queryClient.invalidateQueries({ queryKey: ['permissions'] });
        },
    });
}
